import * as React from 'react';
import { Modal, Progress, Table } from '@roo/roo';
import { postSignNoToast, postWithdrowNoToast, SignItem } from '../api';
import Util from '../util';

interface IProps {
    signData: any;
    providerId: number;
    onCancel: (reload?: boolean) => void;
    getAllSelect: () => string[];
    distributeList: SignItem[];
    columns: any;
    batchOperation: number;
}

interface ErrorItem {
    idx: string;
    failReason: string;
}

interface ResultData {
    errorList: ErrorItem[];
    successList: string[];
}

const { useState, useEffect, useRef } = React;

const getStatusStr = (ref: React.MutableRefObject<ResultData>, selectList: string[]) => {
    const { errorList, successList } = ref.current;
    if (errorList.length + successList.length < selectList.length) {
        return '请求中，请勿关闭页面';
    }
    return '请求完毕';
};

const getErrorList = (ref: React.MutableRefObject<ResultData>, distributeList: SignItem[]) =>
    ref.current.errorList.map(({ idx, failReason }) => {
        const item = distributeList[idx];
        item.failReason = failReason;
        return item;
    });

const SignBatchProgressModal: React.FC<IProps> = ({
    providerId,
    onCancel,
    signData,
    getAllSelect,
    distributeList,
    columns,
    batchOperation,
}) => {
    const [progress, setProgress] = useState(0);
    const selectList = getAllSelect();
    const columnsWithError = columns.slice(0);
    columnsWithError.push({
        prop: 'failReason',
        label: '失败原因',
    });
    const resultRef = useRef<ResultData>({ errorList: [], successList: [] });
    useEffect(() => {
        selectList.reduce(async (pre: Promise<any>, next) => {
            const { errorList, successList } = resultRef.current;
            let promise;
            return pre
                .then(() => {
                    if (batchOperation === 1) {
                        const signItem = distributeList[next];
                        promise = postSignNoToast({
                            providerId,
                            distributeId: signItem.id,
                            data: {
                                ...signData,
                                signType: Util.signTypeGenerate(
                                    signItem.contractType,
                                    signItem.customerSource,
                                ),
                            },
                        });
                    } else {
                        promise = postWithdrowNoToast({
                            providerId,
                            distributeId: distributeList[next].id,
                        });
                    }
                    return promise.then(
                        () => {
                            resultRef.current.successList.push(next);
                        },
                        e => {
                            resultRef.current.errorList.push({
                                idx: next,
                                failReason: e.message,
                            });
                        },
                    );
                })
                .then(() => {
                    // eslint-disable-next-line max-len
                    const nProgress = Math.ceil(
                        ((errorList.length + successList.length) / selectList.length) * 100,
                    );
                    setProgress(nProgress);
                });
        }, Promise.resolve());
    }, [batchOperation, distributeList, providerId, selectList, signData]);

    return (
        <Modal
            visible
            onCancel={() => onCancel(false)}
            showCancelButton
            cancelButtonProps={{
                disabled: progress !== 100,
                type: 'normal',
            }}
            cancelText="关闭"
            showConfirmButton={false}
            backdropClosable={false}
            className="signbatch-modal"
        >
            <h1>{getStatusStr(resultRef, selectList)}</h1>
            <Progress percentage={progress} height={20} showText textInside animationDuration={3} />
            <p>
                成功：{resultRef.current.successList.length}, 失败：
                {resultRef.current.errorList.length}
            </p>
            {progress !== 100 || !resultRef.current.errorList.length ? null : (
                <Table
                    rowKey="id"
                    columns={columnsWithError}
                    data={getErrorList(resultRef, distributeList)}
                />
            )}
        </Modal>
    );
};

export default SignBatchProgressModal;
